// src/pages/clients/Clients.jsx
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BusinessCenter as BusinessIcon, 
  Edit as EditIcon,
  Analytics as AnalyticsIcon,
  Layers as BrandsIcon,
  Delete as DeleteIcon,
  Campaign as CampaignIcon,
  AutoAwesome as MagicIcon,
  ContentPaste as ContentIcon,
  Category as IndustryIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { Box, IconButton, Typography, Tooltip } from '@mui/material';

// Import components
import EntityListContainer from '../../components/common/EntityListContainer';
import { ClientCard } from '../../components/ui';
import SecureAvatar from '../../components/ui/SecureAvatar';
import EntityStatusBadge from '../../components/core/EntityStatusBadge';
import useConfirmDialog from '../../hooks/useConfirmationDialog';

// Import stores
import useAuthStore from '../../stores/authStore';
import useClientStore from '../../stores/clientStore';
import useNotificationStore from '../../stores/notificationStore';

// Import theme hooks
import { useThemeTokens } from '../../theme/themeHooks';

// Configuration
const FILTER_CONFIG = [
  {
    name: 'status',
    label: 'Status',
    allLabel: 'All Statuses',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
      { value: 'onboarding', label: 'Onboarding' },
      { value: 'paused', label: 'Paused' },
      { value: 'former', label: 'Former' }
    ]
  },
  {
    name: 'region',
    label: 'Region',
    allLabel: 'All Regions',
    options: [
      { value: 'Egypt', label: 'Egypt' },
      { value: 'Saudi Arabia', label: 'Saudi Arabia' },
      { value: 'Qatar', label: 'Qatar' },
      { value: 'UAE', label: 'UAE' },
      { value: 'Kuwait', label: 'Kuwait' },
      { value: 'Bahrain', label: 'Bahrain' },
      { value: 'Oman', label: 'Oman' },
      { value: 'Other', label: 'Other' }
    ]
  },
  {
    name: 'industry',
    label: 'Industry',
    allLabel: 'All Industries',
    options: [
      { value: 'Luxury', label: 'Luxury' },
      { value: 'Hospitality', label: 'Hospitality' },
      { value: 'Finance', label: 'Finance' },
      { value: 'Technology', label: 'Technology' },
      { value: 'E-commerce', label: 'E-commerce' },
      { value: 'Healthcare', label: 'Healthcare' },
      { value: 'Education', label: 'Education' },
      { value: 'Real Estate', label: 'Real Estate' },
      { value: 'Food & Beverage', label: 'Food & Beverage' },
      { value: 'Automotive', label: 'Automotive' },
      { value: 'Professional Services', label: 'Professional Services' },
      { value: 'Other', label: 'Other' }
    ]
  }
];

const SORT_OPTIONS = [
  { field: 'name', label: 'Name' },
  { field: 'createdAt', label: 'Date Created' },
  { field: 'region', label: 'Region' },
  { field: 'industry', label: 'Industry' },
  { field: 'status', label: 'Status' }
];

/**
 * Clients listing page
 */
const Clients = () => {
  const navigate = useNavigate();
  
  // Store hooks
  const { hasPermission } = useAuthStore();
  const { clients, meta, isLoading, error, fetchClients, deleteClient } = useClientStore();
  const { showSuccess, showError } = useNotificationStore();
  
  // Theme hooks
  const { getCardStyle, tokens } = useThemeTokens();
  
  // Confirmation dialog
  const [ConfirmDialog, confirm] = useConfirmDialog();
  
  // Check permissions
  const canCreate = hasPermission('clients', 'create');
  const canUpdate = hasPermission('clients', 'update');
  const canDelete = hasPermission('clients', 'delete');
  
  // Handle client deletion with confirmation
  const handleDeleteClient = useCallback(async (client) => {
    const confirmed = await confirm({
      title: `Delete ${client.name}?`,
      content: `This will permanently delete the client and all associated data. This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteClient(client._id);
        showSuccess(`Client "${client.name}" deleted successfully`);
      } catch (error) {
        showError(`Failed to delete client: ${error.message}`);
      }
    }
  }, [confirm, deleteClient, showSuccess, showError]);
  
  // No longer need menu handling functions since we're using the built-in menu
  
  // Entity renderer for grid view
  const renderClient = useCallback((client) => {
    // Safety check
    if (!client || !client._id) return null;
    
    // Debug log to see client data structure
    console.log('Client data:', client);
    
    // Calculate counts safely
    const brandCount = client.brands?.length || client.brandCount || 0;
    const campaignCount = client.campaigns?.length || client.campaignCount || 0;
    const contentCount = client.content?.length || client.contentCount || 0;
    
    return (
      <ClientCard
        client={client}
        linkTo={`/clients/${client._id}`}
        primaryAction={{
          label: 'View',
          icon: <ViewIcon />,
          onClick: () => navigate(`/clients/${client._id}`),
          variant: 'contained',
          color: 'primary'
        }}
        onViewBrands={() => navigate(`/clients/${client._id}/brands`)}
        onAnalytics={() => navigate(`/analytics/clients/${client._id}`)}
        onEdit={canUpdate ? () => navigate(`/clients/${client._id}/edit`) : undefined}
        onDelete={canDelete ? () => handleDeleteClient(client) : undefined}
        stats={[
          { value: brandCount, label: 'Brands', icon: <BrandsIcon fontSize="small" /> },
          { value: campaignCount, label: 'Campaigns', icon: <CampaignIcon fontSize="small" /> },
          { value: contentCount, label: 'Content', icon: <ContentIcon fontSize="small" /> }
        ]}
      />
    );
  }, [canUpdate, canDelete, navigate, handleDeleteClient]);

  // Define table columns
  const tableColumns = [
    { 
      id: 'name', 
      label: 'Client Name',
      renderCell: (client) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecureAvatar
            src={client.logo}
            alt={client.name}
            version="thumbnail"
            sx={{ width: 40, height: 40 }}
          >
            {client.name?.charAt(0) || ''}
          </SecureAvatar>
          <Typography variant="body1" fontWeight={600}>
            {client.name}
          </Typography>
          {client.magicalElements?.enchantmentLevel && (
            <Tooltip title="Enhanced with magical elements">
              <MagicIcon color="secondary" fontSize="small" sx={{ ml: 1 }} />
            </Tooltip>
          )}
        </Box>
      )
    },
    { 
      id: 'status', 
      label: 'Status', 
      align: 'center',
      renderCell: (client) => (
        <EntityStatusBadge
          status={client.status}
          entityType="client"
          size="small"
          variant="outlined"
        />
      )
    },
    { 
      id: 'region', 
      label: 'Region',
      align: 'center',
      renderCell: (client) => (
        <EntityStatusBadge
          status={client.region}
          size="small"
          variant="outlined"
        />
      )
    },
    { 
      id: 'industry', 
      label: 'Industry',
      align: 'center',
      renderCell: (client) => (
        <EntityStatusBadge
          status={client.industry}
          icon={<IndustryIcon fontSize="small" />}
          size="small"
          variant="outlined"
        />
      )
    },
    { 
      id: 'stats', 
      label: 'Assets',
      align: 'center',
      renderCell: (client) => {
        // Calculate counts safely
        const brandCount = client.brands?.length || client.brandCount || 0;
        const campaignCount = client.campaigns?.length || client.campaignCount || 0;
        const contentCount = client.content?.length || client.contentCount || 0;
        
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">Brands</Typography>
              <Typography variant="body1" fontWeight={500}>{brandCount}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">Campaigns</Typography>
              <Typography variant="body1" fontWeight={500}>{campaignCount}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">Content</Typography>
              <Typography variant="body1" fontWeight={500}>{contentCount}</Typography>
            </Box>
          </Box>
        );
      }
    }
  ];

  // Configure table action buttons for table view
  const getTableRowActions = useCallback((client) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="View Brands">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/clients/${client._id}/brands`);
            }}
            
            sx={{ borderRadius: '50%' }}
          >
            <BrandsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Analytics">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/analytics/clients/${client._id}`);
            }}
            
            sx={{ borderRadius: '50%' }}
          >
            <AnalyticsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        {canUpdate && (
          <Tooltip title="Edit">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/clients/${client._id}/edit`);
              }}
              
              sx={{ borderRadius: '50%' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton 
              size="small" 
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClient(client);
              }}
              sx={{ borderRadius: '50%' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [canUpdate, canDelete, navigate, handleDeleteClient]);
  
  return (
    <>
      <EntityListContainer
        title="Clients"
        description="Manage your client portfolio and their marketing assets"
        fetchData={fetchClients}
        renderEntity={renderClient}
        initialFilters={{
          status: 'all',
          region: 'all',
          industry: 'all',
        }}
        initialSort={{ field: 'name', direction: 'asc' }}
        filterConfig={FILTER_CONFIG}
        sortOptions={SORT_OPTIONS}
        
        // View options
        availableViews={['grid', 'table']}
        defaultView="grid"
        
        // Table configuration
        tableConfig={{
          columns: tableColumns,
          onRowClick: (client) => navigate(`/clients/${client._id}`),
          renderCustomRow: null,
          getRowData: null,
          renderRowActions: getTableRowActions
        }}
        
        // Use direct store values
        entities={clients}
        isLoading={isLoading}
        error={error}
        metadata={meta}
        
        actions={{
          canCreate,
          createPath: '/clients/create',
          createLabel: 'Create Client'
        }}
        emptyState={{
          title: 'No Clients Yet',
          description: 'Get started by adding your first client to manage their brands and campaigns.',
          icon: <BusinessIcon sx={{ fontSize: 64 }} />,
          getFilteredTitle: () => 'No Matching Clients',
          getFilteredDescription: () => 'Try adjusting your search or filters to find what you\'re looking for.'
        }}
      />
      
      <ConfirmDialog />
    </>
  );
};

export default Clients;